// The notification window: what arrived, hovering by the bell, and the
// application's error toasts. Swiped sideways by its header, the window
// follows the finger and leaves on that side, running its bulk action; a row
// is swiped away on its own. With one row left, the row carries the window
// away and acts on itself alone.
import { useRef } from "react";
import { useSwipe } from "@/platform/client";
import { IconClock, IconX, SwipeableRow, cx, type SwipeableRowHandle } from "@/platform/ui";
import { PanelHeader, PanelRow, type PanelItem } from "@/notifications/ui/panel.js";

interface WindowItem extends PanelItem {
  /** What swiping the row away does. */
  onDismiss(): void;
  /** Present where the row also offers Snooze and Dismiss on hover. */
  onSnooze?(): void;
}

type Remove = (action: () => void) => void;

function Row({
  item,
  swipeable,
  removeWindow,
}: {
  item: WindowItem;
  /** False for the lone row, which the window moves instead. */
  swipeable: boolean;
  removeWindow: Remove;
}) {
  const handle = useRef<SwipeableRowHandle>(null);
  const remove: Remove = (action) =>
    swipeable && handle.current ? handle.current.leave(action) : removeWindow(action);

  const row = (
    <PanelRow
      item={item}
      aside={
        item.onSnooze && (
          // Shown on hover or focus; a touch screen swipes instead.
          <div className="flex items-center gap-0.5 opacity-0 focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:hidden">
            <button
              onClick={() => remove(item.onSnooze!)}
              title="Snooze 10 min"
              className="rounded-md p-1 text-faint hover:text-ink"
              aria-label="Snooze"
            >
              <IconClock size={13} />
            </button>
            <button
              onClick={() => remove(item.onDismiss)}
              title="Dismiss"
              className="rounded-md p-1 text-faint hover:text-ink"
              aria-label="Dismiss"
            >
              <IconX size={13} />
            </button>
          </div>
        )
      }
    />
  );

  if (!swipeable) return row;
  const dismiss = { onTrigger: item.onDismiss, exits: true };
  return (
    <SwipeableRow ref={handle} onLeft={dismiss} onRight={dismiss}>
      {row}
    </SwipeableRow>
  );
}

export function NotificationWindow({
  className,
  label,
  items,
  onDismissAll,
  onClose,
  onSeeAll,
}: {
  /** Where the window sits. */
  className: string;
  /** The bulk action's label. */
  label: string;
  items: WindowItem[];
  /** The bulk action, run when the window is swiped away by its header. */
  onDismissAll(): void;
  /** Runs once the window has been swiped away. */
  onClose?(): void;
  onSeeAll?(): void;
}) {
  // What the window's leaving does: null is the bulk action.
  const pending = useRef<(() => void) | null>(null);
  const swipe = useSwipe<HTMLDivElement>({
    onCommit() {
      const action = pending.current;
      pending.current = null;
      if (action) action();
      else onDismissAll();
      onClose?.();
    },
  });

  /** The grab handlers, for an area whose swipe runs `action`. */
  const grab = (action: (() => void) | null) => ({
    ...swipe.handlers,
    onTouchStart(e: Parameters<typeof swipe.handlers.onTouchStart>[0]) {
      pending.current = action;
      swipe.handlers.onTouchStart(e);
    },
  });
  const leaveWith: Remove = (action) => {
    pending.current = action;
    swipe.leave("right");
  };

  const lone = items.length === 1 ? items[0]! : null;
  const leaving = swipe.phase === "leave" || swipe.phase === "gone";

  return (
    <div
      ref={swipe.ref}
      className={cx(
        "overflow-hidden rounded-xl border border-line bg-surface shadow-2xl",
        leaving && "pointer-events-none",
        className,
      )}
    >
      <PanelHeader
        label={label}
        onAction={() => {
          pending.current = null;
          swipe.leave("right");
        }}
        onSeeAll={onSeeAll}
        grab={grab(null)}
      />
      <div
        className="max-h-[60dvh] divide-y divide-line/60 overflow-y-auto overflow-x-hidden"
        style={lone ? { touchAction: "pan-y" } : undefined}
        {...(lone ? grab(lone.onDismiss) : {})}
      >
        {items.map((item) => (
          <Row key={item.id} item={item} swipeable={!lone} removeWindow={leaveWith} />
        ))}
      </div>
    </div>
  );
}
